import styled from "styled-components";
import ReactSlick from "react-slick";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: 3px 3px 9px 3px rgba(229, 233, 240, 0.3);
  border: 1px solid hsla(245, 60%, 96%, 1);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  height: fit-content;
  max-width: 400px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const WireframeMediaBlock = styled.div`
  height: 100%;
  min-height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.greyscale.grey200};
  border: 1px dashed ${({ theme }) => theme.palette.greyscale.grey300};
`;

export const CoverImageContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  // Listing photos are typically ~1040x686; matching this avoids side/bottom crop of the watermark.
  aspect-ratio: 1040 / 686;
  background-color: #ececec;
`;

export const PhotosSlider = styled(ReactSlick)`
  width: 100% !important;
  max-width: 100% !important;
  height: 100% !important;

  .slick-list {
    height: 100%;
    width: 100% !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .slick-track {
    height: 100%;
    width: 100% !important;
    max-width: 100% !important;
    display: flex !important;
  }

  .slick-slide {
    height: 100%;
    width: 100% !important;
    max-width: 100% !important;
    flex-shrink: 0;

    > div {
      height: 100%;
      width: 100% !important;
      background-color: #ececec;
    }

    img {
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      object-fit: contain;
      object-position: center;
      display: block;
    }

    ${WireframeMediaBlock} {
      display: block;
      box-sizing: border-box;
    }
  }

  .slick-prev,
  .slick-next {
    z-index: 2;
    color: ${({ theme }) => theme.palette.secondary.orange500};
    background-color: hsla(0, 0%, 100%, 0.95);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);

    &::before {
      display: none !important;
      content: none !important;
    }

    svg {
      color: ${({ theme }) => theme.palette.secondary.orange500};
      opacity: 1;
    }

    &:hover,
    &:focus,
    &:active {
      color: ${({ theme }) => theme.palette.secondary.orange500};
      background-color: hsl(0, 0%, 100%);
      outline: none;

      svg {
        color: ${({ theme }) => theme.palette.secondary.orange500};
        opacity: 1;
      }
    }

    &.slick-disabled {
      opacity: 0.4;
      cursor: default;
    }
  }

  .slick-dots {
    bottom: ${10 / 16}rem;
    padding: 0 ${8 / 16}rem;
    width: 100%;
    margin: 0;
    list-style: none;
    text-align: center;

    li {
      margin: 0 ${3 / 16}rem;
      width: auto;
      height: auto;
    }

    li button {
      width: ${10 / 16}rem;
      height: ${10 / 16}rem;
      padding: 0;
    }

    li button::before {
      width: ${10 / 16}rem;
      height: ${10 / 16}rem;
      font-size: ${8 / 16}rem;
      line-height: ${10 / 16}rem;
      color: hsl(0, 0%, 100%);
      opacity: 0.45;
    }

    li.slick-active button::before {
      color: ${({ theme }) => theme.palette.secondary.orange500};
      opacity: 1;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    .slick-dots {
      bottom: ${8 / 16}rem;

      li button::before {
        font-size: ${7 / 16}rem;
      }
    }
  }
`;

export const PropertyInfosContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 10px 16px;
  font-family: "Inter", sans-serif;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    padding: 10px 8px 14px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    padding: 8px 6px 12px;
  }
`;

export const PriceFavWrapper = styled.div`
  display: flex;
  align-items: center;
  --icon-size: ${24 / 16}rem;
  
  svg {
    width: var(--icon-size);
    height: var(--icon-size);
    color: ${({ theme }) => theme.palette.black.default};
  }
  
  img {
    width: var(--icon-size);
    height: var(--icon-size);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    --icon-size: ${22 / 16}rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    --icon-size: ${20 / 16}rem;
  }
`;

export const Price = styled.p`
  font-size: ${16 / 16}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.primary.blue500};
  margin-right: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    font-size: ${15 / 16}rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    font-size: ${14 / 16}rem;
  }
`;

export const Title = styled.h4`
  color: ${({ theme }) => theme.palette.primary.blue900};
  font-size: ${14 / 16}rem;
  font-weight: 600;
  margin-top: 6px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-height: calc(1.3em * 2);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    font-size: ${13 / 16}rem;
    margin-top: 4px;
    line-height: 1.25;
    min-height: calc(1.25em * 2);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    font-size: ${12 / 16}rem;
    margin-top: 3px;
  }
`;

export const Address = styled.p`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.greyscale.grey700};
  font-size: ${12 / 16}rem;
  margin-top: 6px;
  opacity: 0.5;
  line-height: 1.3;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    font-size: ${11 / 16}rem;
    margin-top: 4px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    font-size: ${10 / 16}rem;
    margin-top: 3px;
  }
`;

export const PropertyDetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1.5px solid hsl(245, 60%, 96%);
  margin-top: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    padding-top: 8px;
    margin-top: 8px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    padding-top: 6px;
    margin-top: 6px;
  }
`;

export const PropertyDetailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  p {
    color: ${({ theme }) => theme.palette.black.default};
    font-size: ${12 / 16}rem;
    font-weight: 500;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
      font-size: ${11 / 16}rem;
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
      font-size: ${10 / 16}rem;
    }
  }
  
  svg {
    color: ${({ theme }) => theme.palette.secondary.orange500};
    width: ${16 / 16}rem;
    height: ${16 / 16}rem;
    flex-shrink: 0;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
      width: ${15 / 16}rem;
      height: ${15 / 16}rem;
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
      width: ${14 / 16}rem;
      height: ${14 / 16}rem;
    }
  }
`;

export const ImageContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const NoPhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const FavoriteLoadingImage = styled.img`
  width: ${24 / 16}rem;
  height: ${24 / 16}rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    width: ${22 / 16}rem;
    height: ${22 / 16}rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    width: ${20 / 16}rem;
    height: ${20 / 16}rem;
  }
`;

export const FallbackImage = styled.img`
  object-fit: cover;
`;

export const SliderArrowButton = styled.button`
  &&.slick-prev,
  &&.slick-next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: ${28 / 16}rem;
    height: ${28 / 16}rem;
    border-radius: 50%;
    border: none;
    padding: 0;
    display: flex !important;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    line-height: inherit;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.secondary.orange500};
    background-color: hsla(0, 0%, 100%, 0.95);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
    transition: box-shadow 0.2s ease;

    &::before {
      display: none !important;
      content: none !important;
    }

    svg {
      color: ${({ theme }) => theme.palette.secondary.orange500};
      opacity: 1;
      flex-shrink: 0;
    }

    &:hover,
    &:focus,
    &:active {
      color: ${({ theme }) => theme.palette.secondary.orange500};
      background-color: hsl(0, 0%, 100%);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      outline: none;

      svg {
        color: ${({ theme }) => theme.palette.secondary.orange500};
        opacity: 1;
      }
    }

    &.slick-disabled {
      opacity: 0.4;
      cursor: default;
    }
  }

  &&.slick-prev {
    left: ${10 / 16}rem;
  }

  &&.slick-next {
    right: ${10 / 16}rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    &&.slick-prev,
    &&.slick-next {
      width: ${26 / 16}rem;
      height: ${26 / 16}rem;
    }
  }
`;